import React, { useEffect, useRef } from 'react';
import { Check, Star } from 'lucide-react';
import { track } from '../../utils/analytics';

interface PricingSectionProps {
  onOpenAuth: (mode: 'login' | 'register') => void;
}

const plans = [
  {
    name: 'Free Trial',
    price: '0',
    period: '2 weeks',
    note: 'Cancel anytime',
    features: ['Full access for 2 weeks', 'AI workout plan generator', 'AI calorie counter', 'Gym finder & walking tracker', '30-day money back guarantee'],
    cta: 'Start Free Trial',
    highlight: false,
    dotColor: 'bg-[#a8abb3]',
  },
  {
    name: 'Yearly',
    price: '99.99',
    period: 'per year',
    note: 'Only €8.33/month',
    priceBadge: 'Save 24%',
    features: ['Best value — 2 months FREE', 'All AI features unlimited', 'Priority support', 'Best for committed gym goers', '30-day money back guarantee'],
    cta: 'Go Pro — Best Deal',
    highlight: true,
    badge: 'Best Value',
    dotColor: 'bg-[#cafd00]',
  },
  {
    name: 'Monthly',
    price: '10.99',
    period: 'per month',
    note: 'Cancel anytime',
    features: ['Full gym tracker access', 'AI nutrition features', 'All workout tools', 'Flexible commitment', '30-day money back guarantee'],
    cta: 'Subscribe',
    highlight: false,
    dotColor: 'bg-[#a8abb3]',
  },
];

export function PricingSection({ onOpenAuth }: PricingSectionProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const firedRef = useRef(false);

  // Fire pricing_viewed once when the section first scrolls into view.
  // This is the funnel's most important denominator — without it we
  // can't measure pricing → checkout conversion.
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting && !firedRef.current) {
          firedRef.current = true;
          track('pricing_viewed');
        }
      });
    }, { threshold: 0.3 });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="pt-12 pb-24 px-6 sm:px-8 relative" id="pricing">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#a8abb3] font-bold block mb-4">Pricing</span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-headline font-bold tracking-tighter text-[#f1f3fc] leading-tight">
            Don&rsquo;t waste money on<br />
            <span className="text-[#cafd00]">multiple apps</span> to track your progress.
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {plans.map((plan, idx) => (
            <div
              key={idx}
              className={`relative rounded-[32px] p-8 border transition-all ${
                plan.highlight
                  ? 'bg-[#1b2028] border-[#cafd00]/30 md:scale-105 shadow-xl shadow-[#cafd00]/5'
                  : 'bg-[#1b2028] border-[#44484f]/10 hover:border-[#44484f]/30'
              }`}
            >
              {plan.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#cafd00] text-[#0a0e14] text-[10px] font-bold uppercase tracking-widest px-4 py-1 rounded-full shadow-lg">
                  {plan.badge}
                </div>
              )}
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#a8abb3] mb-4 mt-1">{plan.name}</div>
              <div className="flex items-baseline gap-1 mb-1 flex-wrap">
                <span className="text-[10px] text-[#a8abb3]">€</span>
                <span className="text-5xl font-headline font-bold tracking-tighter text-[#f1f3fc]">{plan.price}</span>
                {(plan as any).priceBadge && (
                  <span className="ml-2 px-3 py-1.5 rounded-2xl bg-[#cafd00]/15 border border-[#cafd00]/40 text-[#cafd00] text-2xl sm:text-3xl font-headline font-bold tracking-tight self-center">
                    {(plan as any).priceBadge}
                  </span>
                )}
              </div>
              <p className="text-sm text-[#a8abb3] mb-1">{plan.period}</p>
              <p className={`text-xs mb-6 ${plan.highlight ? 'text-[#cafd00] font-bold' : 'text-[#a8abb3]'}`}>{plan.note}</p>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feat, fIdx) => (
                  <li key={fIdx} className="flex items-center gap-3 text-sm text-[#a8abb3]">
                    <div className={`w-1.5 h-1.5 rounded-full ${plan.dotColor} flex-shrink-0`} />
                    {feat}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => {
                  track('pricing_plan_selected', { plan: plan.name.toLowerCase().replace(/\s+/g, '_') });
                  onOpenAuth('register');
                }}
                className={`w-full py-3.5 rounded-full font-bold text-sm transition-all active:scale-95 ${
                  plan.highlight
                    ? 'bg-[#cafd00] text-[#0a0e14] shadow-[0_0_20px_rgba(202,253,0,0.15)]'
                    : 'bg-[#20262f] text-[#f1f3fc] border border-[#44484f]/20 hover:bg-[#262c36]'
                }`}
              >
                {plan.cta}
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
